import * as Sentry from '@sentry/react'

import type { UserInfo } from '@/client/types.gen'
import { isSentrySendPiiEnabled, isSentryUserFeedbackAvailable } from '@/services/sentry'
import { captureUploadRejectionForFeedback } from '@/utils/error'
import type { UnsupportedFileUploadContext } from '@/utils/uploadErrors'

export interface UnsupportedFileUploadFeedbackPayload {
	fileTypeDescription: string
	willingToShare: boolean
	useCase: string
}

function buildFeedbackMessage(
	context: UnsupportedFileUploadContext,
	payload: UnsupportedFileUploadFeedbackPayload
): string {
	const lines = [
		'## feedback_type: unsupported_file_upload',
		`## file_type: ${payload.fileTypeDescription.trim()}`,
		`## file_extension: ${context.fileExtension}`,
		`## content_type: ${context.contentType || 'unknown'}`,
		`## rejection_reason: ${context.rejectionReason}`,
		`## use_case: ${payload.useCase.trim()}`,
		`## server_message: ${context.rawErrorMessage}`,
	]
	return lines.join('\n')
}

function submitUnsupportedFileUploadFeedback(
	context: UnsupportedFileUploadContext,
	payload: UnsupportedFileUploadFeedbackPayload,
	options?: {
		associatedEventId?: string
		name?: string
		email?: string
	}
): string | undefined {
	if (!isSentryUserFeedbackAvailable() || !payload.willingToShare) {
		return undefined
	}

	return Sentry.captureFeedback({
		message: buildFeedbackMessage(context, payload),
		...(options?.name ? { name: options.name } : {}),
		...(options?.email ? { email: options.email } : {}),
		url: window.location.href,
		source: 'unsupported-file-upload',
		...(options?.associatedEventId ? { associatedEventId: options.associatedEventId } : {}),
		tags: {
			feedback_source: 'unsupported-file-upload',
			file_extension: context.fileExtension,
			rejection_reason: context.rejectionReason,
			willing_to_share: 'yes',
		},
	})
}

function resolveFeedbackUserIdentity(user?: UserInfo | null): { name?: string; email?: string } {
	if (!isSentrySendPiiEnabled() || !user) {
		return {}
	}

	const name = [user.firstname, user.lastname].filter(Boolean).join(' ') || undefined
	const email = user.email ?? undefined
	return { name, email }
}

export function fileTypeDescriptionFromContext(
	context: UnsupportedFileUploadContext,
	unknownFileTypeLabel: string
): string {
	if (context.fileExtension !== 'other') {
		return `.${context.fileExtension}`
	}
	return unknownFileTypeLabel
}

export function submitUnsupportedFileUploadFeedbackFromForm(
	context: UnsupportedFileUploadContext,
	payload: {
		fileTypeDescription: string
		useCase: string
		willingToShare: boolean
	},
	user?: UserInfo | null
): string | undefined {
	if (!payload.willingToShare) {
		return undefined
	}

	const associatedEventId = captureUploadRejectionForFeedback(context.rawErrorMessage, {
		rejectionReason: context.rejectionReason,
		fileExtension: context.fileExtension,
		contentType: context.contentType,
	})
	return submitUnsupportedFileUploadFeedback(
		context,
		{
			fileTypeDescription: payload.fileTypeDescription,
			willingToShare: payload.willingToShare,
			useCase: payload.useCase,
		},
		{
			associatedEventId,
			...resolveFeedbackUserIdentity(user),
		}
	)
}
